import { findFieldIndicator } from "@utils/fieldIndicator";
import { useEffect, useRef } from "react";

interface UseScrollSyncOptions {
  /** Ref to the left scrollable container */
  leftRef: React.RefObject<HTMLDivElement | null>;
  /** Ref to the right scrollable container */
  rightRef: React.RefObject<HTMLDivElement | null>;
  /** Field id of the currently active review item */
  activeFieldId: string | null;
}

/** Custom hook for two-way scroll synchronization between left and right panels. */
export const useScrollSync = ({ leftRef, rightRef, activeFieldId }: UseScrollSyncOptions) => {
  // Locks to prevent infinite scroll loop
  const isSyncingLeft = useRef(false);
  const isSyncingRight = useRef(false);

  // Two-way scroll sync effect
  useEffect(() => {
    const left = leftRef.current;
    const right = rightRef.current;

    if (!left || !right) return;

    const handleLeftScroll = () => {
      if (isSyncingLeft.current) return; // left is being driven by right; ignore
      // drive right
      isSyncingRight.current = true;
      right.scrollTop = left.scrollTop;
      // schedule reset after browser flush
      window.setTimeout(() => {
        isSyncingRight.current = false;
      }, 20);
    };

    const handleRightScroll = () => {
      if (isSyncingRight.current) return; // right is being driven by left; ignore
      // drive left
      isSyncingLeft.current = true;
      left.scrollTop = right.scrollTop;
      window.setTimeout(() => {
        isSyncingLeft.current = false;
      }, 20);
    };

    left.addEventListener("scroll", handleLeftScroll, { passive: true });
    right.addEventListener("scroll", handleRightScroll, { passive: true });

    return () => {
      left.removeEventListener("scroll", handleLeftScroll);
      right.removeEventListener("scroll", handleRightScroll);
    };
  }, [leftRef, rightRef]);

  // When a review item is activated, center its field's indicator on the left without echoing the
  // scroll back to the right panel.
  useEffect(() => {
    if (!activeFieldId || !rightRef.current || !leftRef.current) return;

    const leftField = findFieldIndicator(activeFieldId, leftRef.current);
    if (!leftField) return;

    isSyncingLeft.current = true;
    leftField.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    const timer = window.setTimeout(() => {
      isSyncingLeft.current = false;
    }, 200);

    return () => window.clearTimeout(timer);
  }, [activeFieldId, leftRef, rightRef]);

  return {
    isSyncingLeft,
    isSyncingRight,
  };
};
